import { useEffect, useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { add, parseISO, format } from 'date-fns';
import { useSelector } from 'react-redux';

import useForm from '../../../../../lib/hooks/useForm';
import useGetMessage from './useGetMessage';
import { createMessage, updateMessage, updatePost, sendTest } from './data';
import { MESSAGE, SCHEDULED } from '../../../utils/constants';
import { useNotification } from '../../../../../shared/Notification/useNotification';
import { useCampaignContext } from '../../../contexts/CampaignContext';

const generateInitialValues = ({
  message = {},
  t,
  reminderDate,
  reminderSendHour,
  postId,
  publishDate,
  enableReminder,
  sendHour,
  campaignName,
}) => {
  const generateReminderSubject = () =>
    message.subject?.length > 0
      ? t(`reviewModal.messageForm.reminderSubject`, {
          subject: message.subject,
        })
      : t(`reviewModal.messageForm.emptyReminderSubjectDefault`, {
          highlightName: campaignName,
        });

  const initialReminderDate = enableReminder
    ? reminderDate ||
      format(add(parseISO(publishDate), { days: 3 }), 'yyyy-MM-dd')
    : null;

  return {
    id: message.uuid || '',
    subject:
      message.subject ||
      t('reviewModal.messageForm.emptySubjectDefault', {
        highlightName: campaignName,
      }),
    headline:
      message.headline ?? t('reviewModal.messageForm.emptyHeadlineDefault'),
    message:
      message.message ??
      t('reviewModal.messageForm.emptyBodyDefault', {
        highlightName: campaignName,
      }),
    sendSms: message.sendSms || false,
    smsText: message.smsText || '',
    targetedUsers: message.targetedUsers || 'all',
    audienceId: message.audienceId || 'all',
    reminderDate: reminderDate || initialReminderDate,
    reminderSendHour: reminderSendHour || sendHour,
    reminderSubject: message.reminderSubject || generateReminderSubject(),
    postId,
    messageTemplate: message.messageTemplate || 'with_tiles',
    messageCta: message.messageCta || t('reviewModal.messageForm.defaultCta'),
    messageFooter: message.messageFooter || '',
    reminderTemplate: message.reminderTemplate || 'with_tiles',
    reminderCta: message.reminderCta || t('reviewModal.messageForm.defaultCta'),
    reminderFooter: message.reminderFooter || '',
    reminderBody: message.reminderBody || '',
  };
};

export const useMessageForm = ({
  messageId,
  campaignId,
  isModalOpen,
  tab,
  publishDate,
  reminderDate,
  reminderSendHour,
  postId,
  endDate,
  sendHour,
}) => {
  const { email } = useSelector(state => state.currentUser);
  const { message, refetch } = useGetMessage(campaignId, messageId, postId);
  const queryClient = useQueryClient();
  const { t } = useTranslation('posts');
  const { showSuccess } = useNotification();
  const [enableReminder, setEnableReminder] = useState(true);
  const currentBoardName = useSelector(state => state.currentBoard.name);
  const { campaign } = useCampaignContext();
  const campaignName = campaign?.name || currentBoardName;
  const [shouldPopulateText, setShouldPopulateText] = useState(true);
  const [shouldPopulateReminderText, setShouldPopulateReminderText] = useState(
    true,
  );

  useEffect(() => {
    if (isModalOpen) {
      refetch();
    }
  }, [isModalOpen, refetch]);

  const minReminderDate =
    publishDate && add(parseISO(publishDate), { days: 1 });
  const maxReminderDate = endDate && add(parseISO(publishDate), { days: 7 });

  const { mutateAsync: updateMessageMutationAsync } = useMutation(
    updateMessage,
    {
      onSuccess: ({ data }) => {
        queryClient.invalidateQueries(['campaignData', campaignId]);
        queryClient.setQueryData(['message', data.message.uuid], data);
      },
    },
  );

  const { mutateAsync: createMessageMutationAsync } = useMutation(
    createMessage,
    {
      onSuccess: ({ data }) => {
        queryClient.invalidateQueries(['campaignData', campaignId]);
        queryClient.setQueryData(['message', data.message.uuid], data);
      },
    },
  );

  const { mutateAsync: updatePostMutationAsync } = useMutation(updatePost, {
    onSuccess: ({ data }) => {
      queryClient.setQueryData(['campaignData', campaignId], ({ campaign }) => {
        const updatedCampaignPosts = campaign.posts.map(post => {
          if (post.uuid === data.post.uuid) {
            return { ...data.post };
          }
          return post;
        });

        return { campaign: { ...campaign, posts: updatedCampaignPosts } };
      });
    },
  });

  const { mutate: sendTestMutation } = useMutation(sendTest, {
    onSuccess: () => {
      const notificationMsg =
        tab === MESSAGE
          ? t('reviewModal.messageForm.testMsgSent', { email })
          : t('reviewModal.messageForm.testReminderSent', {
              email,
            });

      showSuccess({
        content: notificationMsg,
        title: 'Success',
      });
    },
  });

  const initialValues = generateInitialValues({
    message,
    t,
    reminderDate,
    reminderSendHour,
    postId,
    publishDate,
    enableReminder,
    sendHour,
    campaignName,
  });

  const form = useForm({
    initialValues,
    enableReinitialize: true,
  });

  const onSaveNotification = async ({ showNotification }) => {
    // Only send notification-specific fields, not reminder fields
    // This prevents overwriting reminder values with template defaults
    const notificationData = {
      id: form.values.id,
      subject: form.values.subject,
      headline: form.values.headline,
      message: form.values.message,
      sendSms: form.values.sendSms,
      smsText: form.values.smsText,
      targetedUsers: form.values.targetedUsers,
      audienceId: form.values.audienceId,
      postId: form.values.postId,
      messageTemplate: form.values.messageTemplate,
      messageCta: form.values.messageCta,
      messageFooter: form.values.messageFooter,
    };

    const notificationMessage = messageId
      ? 'Notification Edited Successfully'
      : 'Notification Added Successfully';

    try {
      if (messageId) {
        await updateMessageMutationAsync({
          campaignId,
          postId,
          ...notificationData,
        });
      } else {
        await createMessageMutationAsync({
          campaignId,
          postId,
          ...notificationData,
        });
      }

      if (showNotification) {
        showSuccess({ content: notificationMessage });
      }
    } catch {
      /* empty */
    }
  };

  const onSaveReminder = async ({ showNotification }) => {
    try {
      await updatePostMutationAsync({
        campaignId,
        id: postId,
        reminderDate: form.values.reminderDate,
        reminderSendHour: form.values.reminderSendHour,
        publishDate,
      });

      await updateMessageMutationAsync({
        campaignId,
        postId,
        reminderSubject: form.values.reminderSubject,
        reminderTemplate: form.values.reminderTemplate,
        reminderBody: form.values.reminderBody,
        reminderCta: form.values.reminderCta,
        reminderFooter: form.values.reminderFooter,
        id: form.values.id,
      });

      if (showNotification && form.dirty) {
        const notificationContent = initialValues.reminderDate
          ? 'Reminder Edited Successfully'
          : 'Reminder Added Successfully';

        showSuccess({ content: notificationContent });
      }
    } catch {
      /* empty */
    }
  };

  const schedulePost = async () => {
    try {
      await updatePostMutationAsync({
        campaignId,
        id: postId,
        status: SCHEDULED,
        publishDate,
      });
      showSuccess({ content: 'Post Scheduled Successfully' });
    } catch {
      /* empty */
    }
  };

  const publishPostNow = async () => {
    try {
      await updatePostMutationAsync({
        campaignId,
        id: postId,
        makeLive: true,
      });
      showSuccess({ content: 'Tiles Posted Live Successfully' });
    } catch {
      /* empty */
    }
  };

  const onSendTest = () => {
    sendTestMutation({
      campaignId,
      postId,
      message: form.values,
      template: tab,
    });
  };

  useEffect(() => {
    if (form.values.message !== '') {
      return;
    }

    if (form.values.messageTemplate === 'only_text' && shouldPopulateText) {
      form.setFieldValue(
        'message',
        `Click below to see what's new in ${campaignName} and stay informed.\n\nThese updates are designed to keep you informed and supported.`,
      );

      form.setFieldValue('messageFooter', `Best Regards,\n\nYour HR Team`);

      setShouldPopulateText(false);
    }
  }, [
    form.values.messageTemplate,
    form.values.message,
    form.setFieldValue,
    campaignName,
    shouldPopulateText,
  ]);

  useEffect(() => {
    if (form.values.reminderBody !== '') {
      return;
    }

    if (
      form.values.reminderTemplate === 'only_text' &&
      shouldPopulateReminderText
    ) {
      form.setFieldValue(
        'reminderBody',
        `You still have updates from ${campaignName} to view!\n\nThese quick messages help you stay informed and supported.\n\nClick below to check them out—it only takes a minute.`,
      );

      form.setFieldValue('reminderFooter', `Best Regards,\n\nYour HR Team`);

      setShouldPopulateReminderText(false);
    }
  }, [
    form.values.reminderTemplate,
    form.values.reminderBody,
    campaignName,
    shouldPopulateReminderText,
    form.setFieldValue,
  ]);

  return {
    ...form,
    publishDate,
    message,
    onSaveNotification,
    onSaveReminder,
    minReminderDate,
    maxReminderDate,
    onSendTest,
    campaignId,
    schedulePost,
    publishPostNow,
    enableReminder,
    setEnableReminder,
  };
};

export default useMessageForm;
